import { Controller } from "@hotwired/stimulus"

// Generic controller for auto-updating Turbo Frames when form fields change
// Usage:
//   data-controller="auto-submit"
//   data-auto-submit-target-value="turbo_frame_id"
//   data-auto-submit-url-value="/booking/appointments/time_slots" (optional, for custom endpoint)
//   data-action="change->auto-submit#submit"
export default class extends Controller {
  static values = {
    target: String, // Turbo Frame ID to update
    url: String,    // Optional custom URL for the Turbo Frame (defaults to /booking/appointments/time_slots)
    delay: { type: Number, default: 0 },
    // Optional CSV of element ids whose value should be reset to "" before
    // the frame refresh fires. Used by the Phase 4 booking flow to clear a
    // stale staff_id when the customer switches location, preventing the
    // CreateBookingService :staff_location_mismatch failure.
    clearFields: { type: String, default: "" }
  }

  submit() {
    clearTimeout(this.timeout)

    this.timeout = setTimeout(() => {
      this.resetClearedFields()
      if (this.hasTargetValue) {
        // Update a specific Turbo Frame with new URL
        this.updateFrame()
      } else {
        // Submit the parent form
        this.element.closest("form")?.requestSubmit()
      }
    }, this.delayValue)
  }

  resetClearedFields() {
    if (!this.clearFieldsValue) return
    this.clearFieldsValue.split(",").forEach((id) => {
      const field = document.getElementById(id.trim())
      if (field) field.value = ""
    })
  }

  updateFrame() {
    const frame = document.getElementById(this.targetValue)
    if (!frame) return

    // Get the current form
    const form = this.element.closest("form")
    if (!form) return

    // Build URL params from relevant form fields
    const params = new URLSearchParams()

    // Get the date value - check if this element is a date field, otherwise find it in the form
    let dateValue = this.element.type === "date" ? this.element.value : null
    if (!dateValue) {
      dateValue = form.querySelector("[name='booking_date'], [type='date']")?.value
    }
    if (dateValue) {
      params.set("date", dateValue)
    }

    // Get staff_id from the hidden field
    const staffId = form.querySelector("#appointment_staff_id")?.value
    if (staffId) {
      params.set("staff_id", staffId)
    }

    // Get service_id from the hidden field
    const serviceId = form.querySelector("#appointment_service_id, [name='appointment[service_id]']")?.value
    if (serviceId) {
      params.set("service_id", serviceId)
    }

    // Phase 4: forward location_id when present so the staff_options frame
    // can refetch narrowed to the customer's selected location.
    const locationId = form.querySelector("#appointment_location_id, [name='appointment[location_id]']")?.value
    if (locationId) {
      params.set("location_id", locationId)
    }

    // Required params depend on the target frame: staff_options needs only
    // service_id; time_slots needs both. Bail only when neither is set
    // (nothing useful to fetch in either case).
    if (!serviceId) return
    const targetFrame = this.targetValue
    if (targetFrame !== "staff_options" && !dateValue) return

    // Build the URL for time_slots endpoint (use custom URL if provided)
    const baseUrl = this.hasUrlValue ? this.urlValue : `/booking/appointments/time_slots`
    frame.src = `${baseUrl}?${params.toString()}`
  }
}
